import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { PUBLIC_DEALS_RETAILERS, type PublicDealsRetailer } from "../../config/publicDeals";
import { buildGetStartedUrl, buildGuestDealAuthReturnTo } from "../../utils/authReturn";
import { BASIC_INTRO_LABEL } from "../../utils/basicPlanOffer";
import { HF, liftOnDark, LockIcon, SearchIcon, retailerLabel } from "./homeFeedUi";

/** Chains whose in-store clearance is part of the paid plans. Shown locked to guests. */
export const LOCAL_CHAINS: ReadonlyArray<{ id: string; name: string; color: string }> = [
  { id: "walmart", name: "Walmart", color: "#1D7BF2" },
  { id: "home_depot", name: "HD", color: "#F97316" },
  { id: "lowes", name: "Lowe’s", color: "#1E40AF" },
  { id: "target", name: "Target", color: "#DC2626" },
  { id: "costco", name: "Costco", color: "#E11D48" },
  { id: "bestbuy", name: "Best Buy", color: "#2563EB" },
  { id: "dollar_general", name: "Dollar General", color: "#EAB308" },
  { id: "tractor_supply", name: "Tractor Supply", color: "#B91C1C" },
  { id: "northern_tool", name: "Northern Tool", color: "#DC2626" },
  { id: "samsclub", name: "Sam's Club", color: "#0067A0" },
];

type Filter = "all" | "local" | "online";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

const ONLINE_SORTED = [...PUBLIC_DEALS_RETAILERS].sort((a, b) =>
  retailerLabel(a).localeCompare(retailerLabel(b), "en", { sensitivity: "base" }),
);

const optClass =
  "flex h-9 min-w-0 items-center gap-2 rounded-[10px] px-2.5 text-left text-sm whitespace-nowrap transition-colors hover:bg-[var(--hf-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6]";
const phoneRowClass =
  "flex h-14 items-center gap-3 border-b px-4 no-underline transition-colors last:border-b-0 active:bg-[var(--hf-hover-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#8B5CF6]";

/** A store's initial on a soft tile of its brand color: a light logo stand-in for the phone list. */
function StoreAvatar({ name, color }: { name: string; color: string }) {
  return (
    <span
      aria-hidden="true"
      className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-[9px] text-[14px] font-extrabold"
      style={{ background: `${color}1A`, color: liftOnDark(color) }}
    >
      {name.replace(/[^A-Za-z0-9]/g, "").charAt(0).toUpperCase()}
    </span>
  );
}

const sectionLabel: CSSProperties = { fontSize: 11.5, fontWeight: 800, letterSpacing: "0.8px", color: HF.muted };

export default function RetailerPicker({
  current,
  anchor,
  onSelect,
  onClose,
  onLockedClick,
  lockedTo,
}: {
  current: PublicDealsRetailer;
  /** Where the locked in-store chains go; defaults to sign-up (free members pass the upgrade flow). */
  lockedTo?: string;
  anchor: HTMLElement | null;
  onSelect: (retailer: PublicDealsRetailer) => void;
  onClose: () => void;
  onLockedClick: (chainId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const dialogRef = useRef<HTMLElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [isPhone, setIsPhone] = useState(() =>
    typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 639px)").matches : window.innerWidth < 640,
  );

  useLayoutEffect(() => {
    const place = () => {
      const phone = typeof window.matchMedia === "function"
        ? window.matchMedia("(max-width: 639px)").matches
        : window.innerWidth < 640;
      setIsPhone(phone);
      if (!anchor || phone) return setPosition(null);
      const rect = anchor.getBoundingClientRect();
      const width = Math.min(760, window.innerWidth - 32);
      setPosition({ top: rect.bottom + 8, left: Math.max(16, Math.min(rect.left, window.innerWidth - width - 16)) });
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [anchor]);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // On phones, focusing the search would pop the keyboard over half the list.
    if (isPhone) dialogRef.current?.focus();
    else inputRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      previouslyFocused?.focus();
    };
  }, []);

  const term = query.trim().toLowerCase();
  const local = useMemo(
    () => (filter === "online" ? [] : LOCAL_CHAINS.filter((c) => !term || c.name.toLowerCase().includes(term))),
    [filter, term],
  );
  const online = useMemo(
    () =>
      filter === "local"
        ? []
        : ONLINE_SORTED.filter(
            (r) =>
              !term ||
              [r.name, r.shortName, r.domain, retailerLabel(r)].some((v) => v.toLowerCase().includes(term)),
          ),
    [filter, term],
  );
  const lockedReturn = lockedTo ?? buildGetStartedUrl(buildGuestDealAuthReturnTo("/deals", "local"), "home_feed_picker_local");

  const panelStyle: CSSProperties = isPhone
    ? { position: "fixed", left: 0, right: 0, bottom: 0, height: "90svh", borderRadius: "22px 22px 0 0" }
    : {
        position: "fixed",
        top: position?.top ?? 66,
        left: position?.left ?? 16,
        width: "min(760px, calc(100vw - 32px))",
        maxHeight: `calc(100svh - ${(position?.top ?? 66) + 16}px)`,
        borderRadius: 20,
      };

  return createPortal(
    <div className="fixed inset-0 z-[1000]" style={{ fontFamily: HF.font, color: HF.ink }}>
      <div
        data-testid="retailer-picker-backdrop"
        className="absolute inset-0"
        style={{ background: "var(--hf-overlay)" }}
        onMouseDown={onClose}
      />
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Choose a store"
        tabIndex={-1}
        className="flex flex-col overflow-hidden bg-[var(--hf-surface)] focus:outline-none"
        style={{ ...panelStyle, boxShadow: "0 24px 60px var(--hf-shadow)" }}
      >
        {isPhone && (
          <div className="flex flex-col px-4 pt-2">
            <span className="mx-auto h-[5px] w-10 rounded-full" style={{ background: HF.chipBorder }} aria-hidden="true" />
            <div className="flex items-center pt-2">
              <h2 className="m-0 flex-1 text-[17px] font-bold">Choose a store</h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close store picker"
                className="-mr-1.5 flex h-10 w-10 items-center justify-center rounded-full"
                style={{ background: HF.bg, color: HF.text2 }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>
          </div>
        )}
        <div className={isPhone ? "flex flex-col gap-2.5 px-4 pb-3 pt-2.5" : "flex flex-wrap gap-2.5 p-3.5"} style={{ borderBottom: `1px solid ${HF.hairline}` }}>
          <label
            className={
              isPhone
                ? "flex h-11 min-w-0 items-center gap-2 rounded-xl border px-3 focus-within:border-[#8B5CF6] focus-within:ring-2 focus-within:ring-[var(--hf-violet-soft)]"
                : "flex h-[42px] min-w-0 flex-[1_1_220px] items-center gap-2 rounded-xl px-3 focus-within:ring-2 focus-within:ring-[var(--hf-violet-soft)]"
            }
            style={isPhone ? { background: HF.hover, borderColor: HF.dashed, color: HF.text3 } : { border: "1.5px solid #8B5CF6", color: HF.text3 }}
          >
            <SearchIcon />
            <input
              ref={inputRef}
              type="search"
              aria-label="Search stores"
              placeholder="Search 60+ stores"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="min-w-0 flex-1 border-0 bg-transparent text-[15px] outline-none placeholder:text-[var(--hf-placeholder)]"
              style={{ color: HF.ink }}
            />
          </label>
          <div role="tablist" aria-label="Deal type" className={isPhone ? "grid grid-cols-3 rounded-xl p-[3px]" : "flex rounded-xl p-[3px]"} style={{ background: HF.bg }}>
            {(["all", "local", "online"] as Filter[]).map((value) => {
              const selected = filter === value;
              return (
                <button
                  key={value}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setFilter(value)}
                  className="h-9 rounded-[9px] px-3.5 text-sm"
                  style={{
                    background: selected ? HF.surface : "transparent",
                    boxShadow: selected ? "0 1px 3px var(--hf-shadow)" : undefined,
                    fontWeight: selected ? 700 : 600,
                    color: selected ? HF.brandText : HF.text2,
                  }}
                >
                  {value === "all" ? "All" : value === "local" ? "Local" : "Online"}
                </button>
              );
            })}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]">
          {isPhone && local.length > 0 && (
            <div className="pt-3">
              <div className="flex items-center px-4 pb-1.5">
                <h3 className="m-0 flex-1 text-[13px] font-bold" style={{ color: HF.text2 }}>In-store clearance</h3>
                <span className="text-[12px] font-semibold" style={{ color: HF.amberInk }}>Plans from {BASIC_INTRO_LABEL}</span>
              </div>
              {local.map((chain) => (
                <Link
                  key={chain.id}
                  to={lockedReturn}
                  onClick={() => onLockedClick(chain.id)}
                  className={phoneRowClass}
                  style={{ color: HF.ink, borderColor: HF.hairline }}
                  aria-label={`${chain.name} in-store clearance (locked, sign up to unlock)`}
                >
                  <StoreAvatar name={chain.name} color={chain.color} />
                  <span className="min-w-0 flex-1 truncate text-[15px] font-medium">{chain.name}</span>
                  <span className="flex h-7 items-center gap-1 rounded-full px-2.5 text-[12px] font-bold" style={{ background: HF.amberSoft, color: HF.amberInk }}>
                    <LockIcon size={11} color={HF.amberInk} />
                    Unlock
                  </span>
                </Link>
              ))}
            </div>
          )}

          {isPhone && online.length > 0 && (
            <div className="pb-4 pt-3">
              <h3 className="m-0 px-4 pb-1.5 text-[13px] font-bold" style={{ color: HF.text2 }}>Online stores</h3>
              <div role="listbox" aria-label="Online stores">
                {online.map((retailer) => {
                  const selected = retailer.slug === current.slug;
                  const name = retailerLabel(retailer);
                  return (
                    <button
                      key={retailer.slug}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => onSelect(retailer)}
                      className={`${phoneRowClass} w-full text-left`}
                      style={{ color: HF.ink, borderColor: HF.hairline, background: selected ? HF.violetSoft : undefined }}
                    >
                      <StoreAvatar name={name} color={retailer.accent} />
                      <span className="min-w-0 flex-1 truncate text-[15px]" style={{ fontWeight: selected ? 700 : 500 }}>{name}</span>
                      {selected && (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={HF.brandText} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {!isPhone && local.length > 0 && (
            <div className="flex flex-col gap-1.5 px-3.5 pb-1 pt-3">
              <span style={sectionLabel}>IN-STORE CLEARANCE · PLANS FROM {BASIC_INTRO_LABEL}</span>
              <div className="grid grid-cols-2 gap-0.5 sm:grid-cols-4 md:grid-cols-5">
                {local.map((chain) => (
                  <Link
                    key={chain.id}
                    to={lockedReturn}
                    onClick={() => onLockedClick(chain.id)}
                    className={optClass}
                    style={{ color: HF.text2 }}
                    aria-label={`${chain.name} in-store clearance (locked, sign up to unlock)`}
                  >
                    <span className="min-w-0 flex-1 truncate">{chain.name}</span>
                    <LockIcon size={12} color={HF.amberText} />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {!isPhone && online.length > 0 && (
            <div
              className="flex flex-col gap-1.5 px-3.5 pb-3.5 pt-2.5"
              style={local.length > 0 ? { borderTop: `1px solid ${HF.hairline}`, marginTop: 8 } : undefined}
            >
              <span style={sectionLabel}>ONLINE STORES A–Z</span>
              <div role="listbox" aria-label="Online stores" className="grid grid-cols-2 gap-0.5 sm:grid-cols-4 md:grid-cols-5">
                {online.map((retailer) => {
                  const selected = retailer.slug === current.slug;
                  return (
                    <button
                      key={retailer.slug}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => onSelect(retailer)}
                      className={optClass}
                      style={selected ? { background: HF.violetSoft, color: HF.brandText, fontWeight: 700 } : { color: HF.ink }}
                    >
                      <span className="min-w-0 truncate">{retailerLabel(retailer)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {local.length === 0 && online.length === 0 && (
            <p className="px-4 py-10 text-center text-sm" style={{ color: HF.text3 }}>
              No stores match “{query.trim()}”
            </p>
          )}
        </div>
      </section>
    </div>,
    document.body,
  );
}
